/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import DictationService from '../../services/dictationService';
import { RootState } from '../../reducers';
import { trackNewDictation } from '../../middleware/posthogMiddleware';
import { AppDispatch } from '../../recorder/store';

export type DictationMode = 'fast' | 'best' | 'answer';
export type DictationStatus =
  | 'idle'
  | 'recording'
  | 'streaming'
  | 'processing'
  | 'success'
  | 'error';

interface DictationState {
  status: DictationStatus;
  mode: DictationMode;
  transcript: string;
  error: string | null;
  isModalOpen: boolean;
  audioBlob: Blob | null;
  customVocabulary: string;
  skipLLMProcessing: boolean;
  isStreaming: boolean;
  streamingTranscript: string;
  abortController: AbortController | null;
}

const initialState: DictationState = {
  status: 'idle',
  mode: 'fast',
  transcript: '',
  error: null,
  isModalOpen: false,
  audioBlob: null,
  customVocabulary: '',
  skipLLMProcessing: false,
  isStreaming: false,
  streamingTranscript: '',
  abortController: null,
};

// Async thunk for processing audio (LEGACY - kept for compatibility)
export const processAudioDictation = createAsyncThunk<
  string,
  {
    audioBlob: Blob;
    options?: {
      languageCode?: string;
      skipLLMProcessing?: boolean;
      customVocabulary?: string;
      windowContext?: Record<string, any>;
      mode?: DictationMode;
      outputLanguage?: string;
    };
  },
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  'dictation/processAudio',
  async ({ audioBlob, options }, { getState, dispatch }) => {
    const transcript = await DictationService.processAudioDictation(
      audioBlob,
      dispatch,
      options,
    );

    // Automatically copy to clipboard if successful
    if (transcript) {
      await DictationService.copyToClipboard(transcript);
    }

    // Save to dictation history if successful and not empty
    if (transcript && transcript.trim().length > 0) {
      try {
        const state = getState() as RootState;
        const userId = state.settings?.user?._id;

        if (userId) {
          const title = options?.mode
            ? `${
                options.mode.charAt(0).toUpperCase() + options.mode.slice(1)
              } Dictation`
            : 'New Dictation';

          await DictationService.createDictation({
            title,
            text: transcript,
          });
        }
      } catch (error) {
        console.warn('Failed to save dictation to history:', error);
        // Don't fail the main process if saving fails
      }

      // Track successful dictation in PostHog
      dispatch(
        trackNewDictation({
          transcript,
          mode: options?.mode,
        }),
      );
    }

    return transcript;
  },
);

// Async thunk for batch audio dictation (full recording then process)
export const processBatchAudioDictation = createAsyncThunk<
  string,
  {
    audioBlob: Blob;
    options?: {
      languageCode?: string;
      customVocabulary?: string;
      windowContext?: Record<string, any>;
      mode?: DictationMode;
      outputLanguage?: string;
    };
  },
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  'dictation/processBatchAudio',
  async ({ audioBlob, options }, { getState, dispatch }) => {
    const transcript = await DictationService.processBatchAudioDictation(
      audioBlob,
      options,
    );

    // Automatically copy to clipboard if successful
    if (transcript) {
      await DictationService.copyToClipboard(transcript);
    }

    // Save to dictation history if successful and not empty
    if (transcript && transcript.trim().length > 0) {
      try {
        const state = getState() as RootState;
        const userId = state.settings?.user?._id;

        if (userId) {
          const title = options?.mode
            ? `${
                options.mode.charAt(0).toUpperCase() + options.mode.slice(1)
              } Dictation`
            : 'New Dictation';

          await DictationService.createDictation({
            title,
            text: transcript,
          });
        }
      } catch (error) {
        console.warn('Failed to save dictation to history:', error);
      }

      // Track successful dictation in PostHog
      dispatch(
        trackNewDictation({
          transcript,
          mode: options?.mode,
        }),
      );
    }

    return transcript;
  },
);

// Async thunk for streaming audio dictation
export const processStreamingAudioDictation = createAsyncThunk<
  string,
  {
    options?: {
      languageCode?: string;
      skipLLMProcessing?: boolean;
      customVocabulary?: string;
      windowContext?: Record<string, any>;
      mode?: DictationMode;
      outputLanguage?: string;
    };
  },
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  'dictation/processStreamingAudio',
  async ({ options }, { getState, dispatch }) => {
    return new Promise<string>((resolve, reject) => {
      const { dictation } = getState() as RootState;
      const { abortController } = dictation;

      if (!abortController) {
        reject(new Error('No abort controller available'));
        return;
      }

      const callbacks = {
        onTranscriptionUpdate: (text: string) => {
          // We'll define the action later in the slice, so use a direct dispatch approach
          dispatch({
            type: 'dictation/updateStreamingTranscript',
            payload: text,
          });
        },
        onComplete: async (finalText: string) => {
          // Automatically copy to clipboard if successful
          if (finalText) {
            await DictationService.copyToClipboard(finalText);
          }

          // Save to dictation history if successful and not empty
          if (finalText && finalText.trim().length > 0) {
            try {
              const currentState = getState() as RootState;
              const userId = currentState.settings?.user?._id;

              if (userId) {
                const title = options?.mode
                  ? `${
                      options.mode.charAt(0).toUpperCase() +
                      options.mode.slice(1)
                    } Dictation`
                  : 'New Dictation';

                await DictationService.createDictation({
                  title,
                  text: finalText,
                });
              }
            } catch (error) {
              console.warn('Failed to save dictation to history:', error);
              // Don't fail the main process if saving fails
            }

            // Track successful dictation in PostHog
            dispatch(
              trackNewDictation({
                transcript: finalText,
                mode: options?.mode,
              }),
            );
          }

          resolve(finalText);
        },
        onError: (error: string) => {
          reject(new Error(error));
        },
      };

      DictationService.processStreamingAudioDictation(
        abortController,
        callbacks,
        dispatch,
        options,
      ).catch(reject);
    });
  },
);

const dictationSlice = createSlice({
  name: 'dictation',
  initialState,
  reducers: {
    openDictationModal: {
      reducer: (state, action: PayloadAction<DictationMode | undefined>) => {
        state.isModalOpen = true;
        state.status = 'idle';
        state.transcript = '';
        state.error = null;
        // Set mode to payload if provided, otherwise reset to default 'fast'
        state.mode = action.payload || 'fast';
      },
      prepare: (mode?: DictationMode) => ({
        payload: mode,
      }),
    },
    closeDictationModal: (state) => {
      state.isModalOpen = false;
      state.status = 'idle';
      state.audioBlob = null;
    },
    setDictationMode: (state, action: PayloadAction<DictationMode>) => {
      state.mode = action.payload;
    },
    setCustomVocabulary: (state, action: PayloadAction<string>) => {
      state.customVocabulary = action.payload;
    },
    setSkipLLMProcessing: (state, action: PayloadAction<boolean>) => {
      state.skipLLMProcessing = action.payload;
    },
    startRecording: (state) => {
      state.status = 'recording';
      state.transcript = '';
      state.error = null;
    },
    startStreamingRecording: (state) => {
      state.status = 'recording'; // Show as recording to user for transparency
      state.isStreaming = true;
      state.transcript = '';
      state.streamingTranscript = '';
      state.error = null;
      state.abortController = new AbortController();
    },
    updateStreamingTranscript: (state, action: PayloadAction<string>) => {
      state.streamingTranscript = action.payload;
    },
    stopStreamingRecording: (state) => {
      if (state.abortController) {
        state.abortController.abort();
        state.abortController = null;
      }
      // Completely reset to initial state
      state.status = 'idle';
      state.isStreaming = false;
      state.streamingTranscript = '';
      state.transcript = '';
      state.error = null;
    },
    stopRecording: (state, action: PayloadAction<Blob>) => {
      state.status = 'processing';
      state.audioBlob = action.payload;
    },
    resetDictation: (state) => {
      if (state.abortController) {
        state.abortController.abort();
        state.abortController = null;
      }
      state.status = 'idle';
      state.transcript = '';
      state.streamingTranscript = '';
      state.error = null;
      state.audioBlob = null;
      state.isStreaming = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(processAudioDictation.pending, (state) => {
        state.status = 'processing';
        state.error = null;
      })
      .addCase(processAudioDictation.fulfilled, (state, action) => {
        state.status = 'success';
        state.transcript = action.payload;
        state.error = null;
      })
      .addCase(processAudioDictation.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message || 'Failed to process dictation';
      })
      .addCase(processStreamingAudioDictation.pending, (state) => {
        // Keep recording status during processing for transparency
        if (state.status !== 'recording') {
          state.status = 'processing';
        }
        state.error = null;
      })
      .addCase(processStreamingAudioDictation.fulfilled, (state, action) => {
        state.status = 'success';
        state.transcript = action.payload;
        state.streamingTranscript = '';
        state.error = null;
        state.isStreaming = false;
        if (state.abortController) {
          state.abortController = null;
        }
      })
      .addCase(processStreamingAudioDictation.rejected, (state, action) => {
        state.status = 'error';
        state.error =
          action.error.message || 'Failed to process streaming dictation';
        state.isStreaming = false;
        state.streamingTranscript = '';
        if (state.abortController) {
          state.abortController = null;
        }
      })
      .addCase(processBatchAudioDictation.pending, (state) => {
        state.status = 'processing';
        state.error = null;
      })
      .addCase(processBatchAudioDictation.fulfilled, (state, action) => {
        state.status = 'success';
        state.transcript = action.payload;
        state.audioBlob = null;
        state.error = null;
      })
      .addCase(processBatchAudioDictation.rejected, (state, action) => {
        state.status = 'error';
        state.error =
          action.error.message || 'Failed to process batch dictation';
        state.audioBlob = null;
      });
  },
});

export const {
  openDictationModal,
  closeDictationModal,
  setDictationMode,
  setCustomVocabulary,
  setSkipLLMProcessing,
  startRecording,
  stopRecording,
  startStreamingRecording,
  updateStreamingTranscript,
  stopStreamingRecording,
  resetDictation,
} = dictationSlice.actions;

export default dictationSlice.reducer;
